'use client'

import { Image } from '@vinted/web-ui'

import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'
import {
  UserThumbnailSize,
  findThumbnail,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { conversationUserProfileIconClick } from '@marketplace-web/messaging/conversation-user-data'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'

type Props = {
  isSystem: boolean
  isCurrentAuthor?: boolean
  photo: {
    url: string
    thumbnails: Array<{
      type: UserThumbnailSize
      url: string
    }>
  } | null
  profileUrl: string
  alt?: string
}

const UserPhoto = ({ isSystem, isCurrentAuthor = false, photo, profileUrl, alt }: Props) => {
  const { conversation } = useConversationContext()
  const { track } = useTracking()
  const userThumbnailUrl = findThumbnail(photo)
  const asset = useAsset('assets/no-photo')

  const getImageSrc = () => {
    if (isSystem) return asset('user-clothing/system-photo/default.png')
    if (userThumbnailUrl) return userThumbnailUrl

    return asset(EMPTY_USER_IMAGE_NAME)
  }

  const userImage = (
    <Image
      role="img"
      src={getImageSrc()}
      size={Image.Size.Medium}
      styling={Image.Styling.Circle}
      alt={alt}
      testId="user-photo"
    />
  )

  const handleUserPhotoClick = () => {
    if (!conversation?.id || !conversation?.transactionId) return

    track(
      conversationUserProfileIconClick({
        conversationId: conversation.id,
        transactionId: conversation.transactionId,
        userSide: conversation.transaction?.userSide ?? null,
        oppositeUserId: conversation.oppositeUser?.id ?? null,
      }),
    )
  }

  if (isCurrentAuthor) return null

  return profileUrl ? (
    <a href={profileUrl} onClick={handleUserPhotoClick}>
      {userImage}
    </a>
  ) : (
    userImage
  )
}

export default UserPhoto
